import axios from "axios";
import { html } from "lit";
import DOMPurify from "dompurify";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import { globalState } from "./globa-state";
import { enAU, sk } from "date-fns/locale";
import { FieldType } from "@padloc/core/src/item";
import { CreateItemList } from "../../types/strapi/create-item-list.type";
import { FieldDataStrapiType } from "../../types/strapi/field.type";

const loaded: Map<string, Promise<any>> = new Map<string, Promise<any>>();
export function loadScript(src: string, global?: string): Promise<any> {
    if (loaded.has(src)) {
        return loaded.get(src)!;
    }

    const s = document.createElement("script");
    s.src = src;
    s.type = "text/javascript";
    const p = new Promise((resolve, reject) => {
        s.onload = () => resolve(global ? window[global] : undefined);
        s.onerror = (e: any) => reject(e);
        document.head.appendChild(s);
    });

    loaded.set(src, p);
    return p;
}

export async function formatDateFromNow(date: Date | string | number, addSuffix = true) {
    const langState = globalState.getState("lang");
    const { formatDistanceToNow } = await import(/* webpackChunkName: "date-fns" */ "date-fns");
    const isSlovak = langState === "sk";
    const locale = isSlovak ? sk : enAU;
    return formatDistanceToNow(new Date(date), { addSuffix, locale: locale });
}

export function formatDate(date: Date | string | number) {
    return new Intl.DateTimeFormat().format(new Date(date));
}

export function formatDateTime(date: Date | string | number) {
    return new Intl.DateTimeFormat(undefined, {
        dateStyle: "short",
        timeStyle: "medium",
    } as any).format(new Date(date));
}

export async function passwordStrength(pwd: string): Promise<{ score: number }> {
    // @ts-ignore
    const { default: zxcvbn } = await import(/* webpackChunkName: "zxcvbn" */ "zxcvbn");
    return zxcvbn(pwd);
}

export function toggleAttribute(el: Element, attr: string, on: boolean) {
    if (on) {
        el.setAttribute(attr, "");
    } else {
        el.removeAttribute(attr);
    }
}

export function mediaType(mimeType: string) {
    const match = mimeType.match(/(.*)\/(.*)/);
    const [, type, subtype] = match || ["", "", ""];

    switch (type) {
        case "video":
            return "video";
        case "audio":
            return "audio";
        case "image":
            return "image";
        case "text":
            switch (subtype) {
                case "csv":
                // return "csv";
                case "plain":
                    return "text";
                default:
                    return "code";
            }
        case "application":
            switch (subtype) {
                case "pdf":
                    return "pdf";
                case "json":
                    return "code";
                case "pkcs8":
                case "pkcs10":
                case "pkix-cert":
                case "pkix-crl":
                case "pkcs7-mime":
                case "x-x509-ca-cert":
                case "x-x509-user-cert":
                case "x-pkcs12":
                case "x-pkcs7-certificates":
                case "x-pkcs7-mime":
                case "x-pkcs7-crl":
                case "x-pem-file":
                case "x-pkcs12":
                case "x-pkcs7-certreqresp":
                    return "certificate";
                case "zip":
                case "x-7z-compressed":
                case "x-freearc":
                case "x-bzip":
                case "x-bzip2":
                case "java-archive":
                case "x-rar-compressed":
                case "x-tar":
                    return "archive";
            }
        default:
            return "";
    }
}

export function replaceDynamicValueString(str?: string, replacement0?: string, replacement1?: string) {
    if (str) {
        if (replacement0) {
            str = str.replace(/\{0\}/g, replacement0);
        }
        if (replacement1) {
            str = str.replace(/\{1\}/g, replacement1);
        }
        return str;
    } else {
        return "";
    }
}

export function fileIcon(mimeType: string) {
    const mType = mediaType(mimeType);
    return mType ? `file-${mType}` : "file";
}

export function fileSize(size: number = 0) {
    return size < 1e6 ? Math.ceil(size / 10) / 100 + " KB" : Math.ceil(size / 10000) / 100 + " MB";
}

export function mask(value: string): string {
    return value && value.replace(/[^\n]/g, "\u2022");
}

export function isTouch(): boolean {
    return window.matchMedia("(hover: none)").matches;
}

export function openPopup(
    url = "",
    {
        name = "_blank",
        width = 500,
        height = 800,
    }: {
        url?: string;
        name?: string;
        width?: number;
        height?: number;
    } = {}
): Window | null {
    const { outerHeight, outerWidth, screenX, screenY } = window;
    const top = outerHeight / 2 + screenY - height / 2;
    const left = outerWidth / 2 + screenX - width / 2;
    return window.open(
        url,
        name,
        `toolbar=0,scrollbars=1,status=1,resizable=1,location=1,menuBar=0,width=${width},height=${height},top=${top},left=${left}`
    );
}

export function highlightJson(json: string) {
    // Replace &, < and > with html entities
    json = json.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

    json = json.replace(
        /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+\-]?\d+)?)/g,
        function (match) {
            var cls = "number";
            if (/^"/.test(match)) {
                if (/:$/.test(match)) {
                    cls = "key";
                } else {
                    cls = "string";
                }
            } else if (/true|false/.test(match)) {
                cls = "boolean";
            } else if (/null/.test(match)) {
                cls = "null";
            }
            return '<span class="' + cls + '">' + DOMPurify.sanitize(match) + "</span>";
        }
    );

    return html`${unsafeHTML(json)}`;
}

export function selectFile() {
    return new Promise<File | null>((resolve) => {
        const input = document.createElement("input");
        input.style.opacity = "0";
        input.style.position = "absolute";
        input.type = "file";
        document.body.appendChild(input);
        input.addEventListener(
            "change",
            () => {
                resolve(input.files?.[0] || null);
            },
            { once: true }
        );
        input.click();
        document.body.removeChild(input);
    });
}

export function hideSpinner() {
    const spinner = document.querySelector(".spinner") as HTMLElement;
    spinner.style.display = "none";
}

export const fetchStrapiData = async (path: string) => {
    const locale = localStorage.getItem("lang");
    const cacheKey = `strapi_${path}_${locale}`;
    const cacheDuration = 3600 * 1000; // 1 hour
    // Get cached data
    const cached = localStorage.getItem(cacheKey);
    globalState.setState("isLoading", true);
    if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        const now = Date.now();
        if (now - timestamp < cacheDuration) {
            globalState.setState("isLoading", false);
            return data;
        }
    }

    try {
        const res = await axios.get(`${process.env.STRAPI_URL}/api/${path}?populate=deep&locale=all`, {
            headers: {
                "Content-Type": "application/json",
                Authorization: "Bearer " + process.env.STRAPI_TOKEN,
            },
        });

        const data = res;

        // Cache the new data with timestamp
        localStorage.setItem(cacheKey, JSON.stringify({ data, timestamp: Date.now() }));

        return data;
    } catch (error) {
        console.error("Error fetching data:", error);
    } finally {
        globalState.setState("isLoading", false);
    }
};

export const getNewItemTitleHelper = async (item: any) => {
    const template = await getItemTemplates();
    const res = template.filter((templateIcon) => templateIcon?.icon === item?.icon);
    return res.toString();
};

export const getItemField = (strapiData: any, fieldName: string) => {
    if (!strapiData || !fieldName) return fieldName;
    return strapiData[fieldName] || fieldName;
};

export const getFieldDef = (strapiData: FieldDataStrapiType, fieldName: string) => {
    if (!strapiData) return "";
    return (strapiData as any)[fieldName] || "";
};

export const getItemTemplates = async () => {
    const locale = localStorage.getItem("lang");
    const res = await fetchStrapiData("create-item-list");
    const enStrapiData = res?.data?.data?.find((item: any) => item?.attributes?.locale === "en");
    const skStrapiData = res?.data?.data?.find((item: any) => item?.attributes?.locale === "sk");
    const strapiData: CreateItemList =
        locale === "en" ? enStrapiData?.attributes?.items : skStrapiData?.attributes?.items;
    return [
        {
            toString: () => strapiData.websiteApp,
            icon: "web",
            fields: [
                {
                    get name() {
                        return "username";
                    },
                    type: FieldType.Username,
                },
                {
                    get name() {
                        return "password";
                    },
                    type: FieldType.Password,
                },
                {
                    get name() {
                        return "url";
                    },
                    type: FieldType.Url,
                },
            ],
        },
        {
            toString: () => strapiData?.email,
            icon: "email",
            fields: [
                {
                    get name() {
                        return "emailProvider";
                    },
                    type: FieldType.Text,
                },
                {
                    get name() {
                        return "yourEmailAdress";
                    },
                    type: FieldType.Text,
                },
                {
                    get name() {
                        return "password";
                    },
                    type: FieldType.Password,
                },
                {
                    get name() {
                        return "url";
                    },
                    type: FieldType.Url,
                },
                {
                    get name() {
                        return "notes";
                    },
                    type: FieldType.Note,
                },
            ],
        },
        {
            toString: () => strapiData?.computer,
            icon: "desktop",
            fields: [
                {
                    get name() {
                        return "username";
                    },
                    type: FieldType.Username,
                },
                {
                    get name() {
                        return "password";
                    },
                    type: FieldType.Password,
                },
            ],
        },
        {
            toString: () => strapiData?.creditCard,
            icon: "credit",
            fields: [
                {
                    get name() {
                        return "cardNumber";
                    },
                    type: FieldType.Credit,
                },
                {
                    get name() {
                        return "cardOwner";
                    },
                    type: FieldType.Text,
                },
                {
                    get name() {
                        return "validUntil";
                    },
                    type: FieldType.Month,
                },
                {
                    get name() {
                        return "cvc";
                    },
                    type: FieldType.Pin,
                },
                {
                    get name() {
                        return "pin";
                    },
                    type: FieldType.Pin,
                },
            ],
        },
        {
            toString: () => strapiData.bankAccount,
            icon: "bank",
            fields: [
                {
                    get name() {
                        return "accountOwner";
                    },
                    type: FieldType.Text,
                },
                {
                    get name() {
                        return "iban";
                    },
                    type: FieldType.Text,
                },
                {
                    get name() {
                        return "bic";
                    },
                    type: FieldType.Text,
                },
                {
                    get name() {
                        return "cardPIN";
                    },
                    type: FieldType.Pin,
                },
            ],
        },
        {
            toString: () => strapiData?.wifiPassword,
            icon: "wifi",
            fields: [
                {
                    get name() {
                        return "name";
                    },
                    type: FieldType.Text,
                },
                {
                    get name() {
                        return "password";
                    },
                    type: FieldType.Password,
                },
            ],
        },
        {
            toString: () => strapiData?.membership,
            icon: "wifi",
            fields: [
                {
                    get name() {
                        return "organization";
                    },
                    type: FieldType.Text,
                },
                {
                    get name() {
                        return "idNumber";
                    },
                    type: FieldType.Text,
                },
                {
                    get name() {
                        return "username";
                    },
                    type: FieldType.Username,
                },
                {
                    get name() {
                        return "password";
                    },
                    type: FieldType.Password,
                },
                {
                    get name() {
                        return "dateExpiry";
                    },
                    type: FieldType.Date,
                },
                {
                    get name() {
                        return "url";
                    },
                    type: FieldType.Url,
                },
            ],
        },
        {
            toString: () => strapiData?.car,
            icon: "car",
            fields: [
                {
                    get name() {
                        return "brandCar";
                    },
                    type: FieldType.Text,
                },
                {
                    get name() {
                        return "yearProduction";
                    },
                    type: FieldType.Credit,
                },
                {
                    get name() {
                        return "model";
                    },
                    type: FieldType.Text,
                },
                {
                    get name() {
                        return "colour";
                    },
                    type: FieldType.Text,
                },
                {
                    get name() {
                        return "licenceNumber";
                    },
                    type: FieldType.Text,
                },
                {
                    get name() {
                        return "vinNumber";
                    },
                    type: FieldType.Text,
                },
                {
                    get name() {
                        return "insuranceCompany";
                    },
                    type: FieldType.Text,
                },
                {
                    get name() {
                        return "policyNumber";
                    },
                    type: FieldType.Text,
                },
                {
                    get name() {
                        return "loginName";
                    },
                    type: FieldType.Username,
                },
                {
                    get name() {
                        return "password";
                    },
                    type: FieldType.Password,
                },
                {
                    get name() {
                        return "notes";
                    },
                    type: FieldType.Note,
                },
            ],
        },
        {
            toString: () => strapiData?.passport,
            icon: "passport",
            fields: [
                {
                    get name() {
                        return "surname";
                    },
                    type: FieldType.Text,
                },
                {
                    get name() {
                        return "givenNames";
                    },
                    type: FieldType.Text,
                },
                {
                    get name() {
                        return "passportNum";
                    },
                    type: FieldType.Text,
                },
                {
                    get name() {
                        return "nationality";
                    },
                    type: FieldType.Text,
                },
                {
                    get name() {
                        return "dob";
                    },
                    type: FieldType.Date,
                },
                {
                    get name() {
                        return "sex";
                    },
                    type: FieldType.Text,
                },
                {
                    get name() {
                        return "placeBirth";
                    },
                    type: FieldType.Text,
                },
                {
                    get name() {
                        return "dateIssue";
                    },
                    type: FieldType.Date,
                },
                {
                    get name() {
                        return "dateExpiry";
                    },
                    type: FieldType.Date,
                },
                {
                    get name() {
                        return "identificationCardNumber";
                    },
                    type: FieldType.Text,
                },
            ],
        },
        {
            toString: () => strapiData?.IdCard,
            icon: "id-card",
            fields: [
                {
                    get name() {
                        return "surname";
                    },
                    type: FieldType.Text,
                },
                {
                    get name() {
                        return "givenNames";
                    },
                    type: FieldType.Text,
                },
                {
                    get name() {
                        return "identificationCardNumber";
                    },
                    type: FieldType.Text,
                },
                {
                    get name() {
                        return "nationality";
                    },
                    type: FieldType.Text,
                },
                {
                    get name() {
                        return "dob";
                    },
                    type: FieldType.Date,
                },
                {
                    get name() {
                        return "sex";
                    },
                    type: FieldType.Text,
                },
                {
                    get name() {
                        return "issuedBy";
                    },
                    type: FieldType.Text,
                },
                {
                    get name() {
                        return "dateIssue";
                    },
                    type: FieldType.Date,
                },
                {
                    get name() {
                        return "dateExpiry";
                    },
                    type: FieldType.Date,
                },
                {
                    get name() {
                        return "personalNo";
                    },
                    type: FieldType.Credit,
                },
                {
                    get name() {
                        return "canNumber";
                    },
                    type: FieldType.Text,
                },
                {
                    get name() {
                        return "bok";
                    },
                    type: FieldType.Text,
                },
                {
                    get name() {
                        return "pin";
                    },
                    type: FieldType.Text,
                },
                {
                    get name() {
                        return "puk";
                    },
                    type: FieldType.Text,
                },
            ],
        },
        {
            toString: () => strapiData?.healthInsuranceCard,
            icon: "health",
            fields: [
                {
                    get name() {
                        return "healthInsuranceProvider";
                    },
                    type: FieldType.Text,
                },
                {
                    get name() {
                        return "surname";
                    },
                    type: FieldType.Text,
                },
                {
                    get name() {
                        return "givenNames";
                    },
                    type: FieldType.Text,
                },
                {
                    get name() {
                        return "dob";
                    },
                    type: FieldType.Date,
                },
                {
                    get name() {
                        return "personalNo";
                    },
                    type: FieldType.Credit,
                },
                {
                    get name() {
                        return "instituteIdentificationNumber";
                    },
                    type: FieldType.Text,
                },
                {
                    get name() {
                        return "identificationCardNumber";
                    },
                    type: FieldType.Text,
                },
                {
                    get name() {
                        return "dateExpiry";
                    },
                    type: FieldType.Date,
                },
                {
                    get name() {
                        return "dateIssue";
                    },
                    type: FieldType.Date,
                },
                {
                    get name() {
                        return "healthInsuranceRecordContact";
                    },
                    type: FieldType.Phone,
                },
                {
                    get name() {
                        return "healthInsuranceRecordLink";
                    },
                    type: FieldType.Url,
                },
            ],
        },

        {
            toString: () => strapiData?.note,
            icon: "note",
            fields: [
                {
                    get name() {
                        return "note";
                    },
                    type: FieldType.Note,
                },
            ],
        },
        {
            toString: () => strapiData?.authenticator,
            icon: "totp",
            fields: [
                {
                    get name() {
                        return "oneTimePassword";
                    },
                    type: FieldType.Totp,
                },
            ],
        },
        {
            toString: () => strapiData?.document,
            icon: "attachment",
            fields: [],
            attachment: true,
        },
        // {
        //     toString: () => strapiData?.custom,
        //     icon: "custom",
        //     fields: [],
        // },
        {
            toString: () => strapiData?.drivingLicense,
            icon: "steering-wheel",
            fields: [
                {
                    get name() {
                        return "surname";
                    },
                    type: FieldType.Text,
                },
                {
                    get name() {
                        return "givenNames";
                    },
                    type: FieldType.Text,
                },
                {
                    get name() {
                        return "dob";
                    },
                    type: FieldType.Date,
                },
                {
                    get name() {
                        return "placeBirth";
                    },
                    type: FieldType.Text,
                },
                {
                    get name() {
                        return "dateIssue";
                    },
                    type: FieldType.Date,
                },
                {
                    get name() {
                        return "dateExpiry";
                    },
                    type: FieldType.Date,
                },
                {
                    get name() {
                        return "issuedBy";
                    },
                    type: FieldType.Text,
                },
                {
                    get name() {
                        return "drivingLicenceNumber";
                    },
                    type: FieldType.Text,
                },
                {
                    get name() {
                        return "groupsDrivingLicense";
                    },
                    type: FieldType.Text,
                },
            ],
        },
        {
            toString: () => strapiData?.certificateRoadworthiness,
            icon: "road-circle-check",
            fields: [
                {
                    get name() {
                        return "vehicleMake";
                    },
                    type: FieldType.Text,
                },
                {
                    get name() {
                        return "commercialDescription";
                    },
                    type: FieldType.Text,
                },
                {
                    get name() {
                        return "vehicleType";
                    },
                    type: FieldType.Text,
                },
                {
                    get name() {
                        return "vehiclePlate";
                    },
                    type: FieldType.Text,
                },
                {
                    get name() {
                        return "vinNumber";
                    },
                    type: FieldType.Text,
                },
                {
                    get name() {
                        return "inspectionValid";
                    },
                    type: FieldType.Date,
                },
                {
                    get name() {
                        return "dateIssue";
                    },
                    type: FieldType.Date,
                },
            ],
        },
        {
            toString: () => strapiData?.vehicleRegistationCertificate,
            icon: "passport",
            fields: [
                {
                    get name() {
                        return "vehiclePlateNumber";
                    },
                    type: FieldType.Text,
                },
                {
                    get name() {
                        return "dateIssue";
                    },
                    type: FieldType.Date,
                },
                {
                    get name() {
                        return "holderRegistration";
                    },
                    type: FieldType.Text,
                },
                {
                    get name() {
                        return "ownerVehicle";
                    },
                    type: FieldType.Text,
                },
                {
                    get name() {
                        return "adressRegistration";
                    },
                    type: FieldType.Text,
                },
                {
                    get name() {
                        return "code";
                    },
                    type: FieldType.Text,
                },
                {
                    get name() {
                        return "make";
                    },
                    type: FieldType.Text,
                },
                {
                    get name() {
                        return "type";
                    },
                    type: FieldType.Text,
                },
                {
                    get name() {
                        return "commercialDescription";
                    },
                    type: FieldType.Text,
                },
                {
                    get name() {
                        return "vinNumber";
                    },
                    type: FieldType.Text,
                },
            ],
        },
        {
            toString: () => strapiData?.firearmsLicense,
            icon: "gun",
            fields: [
                {
                    get name() {
                        return "givenNames";
                    },
                    type: FieldType.Text,
                },
                {
                    get name() {
                        return "surname";
                    },
                    type: FieldType.Text,
                },
                {
                    get name() {
                        return "personalNo";
                    },
                    type: FieldType.Credit,
                },
                {
                    get name() {
                        return "placeResidence";
                    },
                    type: FieldType.Text,
                },
                {
                    get name() {
                        return "issuedBy";
                    },
                    type: FieldType.Text,
                },
                {
                    get name() {
                        return "classFirearm";
                    },
                    type: FieldType.Text,
                },
                {
                    get name() {
                        return "dateExpiry";
                    },
                    type: FieldType.Date,
                },
            ],
        },
        {
            toString: () => strapiData?.alarm,
            icon: "bell",
            fields: [
                {
                    get name() {
                        return "fullName";
                    },
                    type: FieldType.Text,
                },
                {
                    get name() {
                        return "licenceNumber";
                    },
                    type: FieldType.Text,
                },
            ],
        },
        {
            toString: () => strapiData?.refrigerator,
            icon: "refridgerator",
            fields: [
                {
                    get name() {
                        return "fullName";
                    },
                    type: FieldType.Text,
                },
                {
                    get name() {
                        return "licenceNumber";
                    },
                    type: FieldType.Text,
                },
            ],
        },
        {
            toString: () => strapiData?.washingMachine,
            icon: "washing-machine",
            fields: [
                {
                    get name() {
                        return "fullName";
                    },
                    type: FieldType.Text,
                },
                {
                    get name() {
                        return "licenceNumber";
                    },
                    type: FieldType.Text,
                },
            ],
        },
        {
            toString: () => strapiData?.heatingBoiler,
            icon: "heat",
            fields: [
                {
                    get name() {
                        return "fullName";
                    },
                    type: FieldType.Text,
                },
                {
                    get name() {
                        return "licenseNumber";
                    },
                    type: FieldType.Text,
                },
            ],
        },
        {
            toString: () => strapiData?.aircondition,
            icon: "air-conditioner",
            fields: [
                {
                    get name() {
                        return "fullName";
                    },
                    type: FieldType.Text,
                },
                {
                    get name() {
                        return "licenseNumber";
                    },
                    type: FieldType.Text,
                },
            ],
        },
        {
            toString: () => strapiData?.pills,
            icon: "pills",
            fields: [
                {
                    get name() {
                        return "fullName";
                    },
                    type: FieldType.Text,
                },
                {
                    get name() {
                        return "licenseNumber";
                    },
                    type: FieldType.Text,
                },
            ],
        },
        {
            toString: () => strapiData?.oven,
            icon: "oven",
            fields: [
                {
                    get name() {
                        return "fullName";
                    },
                    type: FieldType.Text,
                },
                {
                    get name() {
                        return "licenseNumber";
                    },
                    type: FieldType.Text,
                },
            ],
        },
    ];
};
